import { OAuthService } from 'angular-oauth2-oidc';
import { API_BASE } from '../../constants/general.constants';
import { AppStateService } from '../app-state/app-state.service';

/**
 * Lightweight LLM bridge service for Python step code.
 *
 * Uses raw `fetch()` instead of Angular HttpClient because the bridge
 * runs outside Angular's zone / change-detection cycle. The API matches
 * the non-streaming chat behavior of `LlmService.operationalChat()` (and
 * related streaming variants) so Python code behaves identically in both
 * runtimes.
 */
export class LlmBridgeService {
  constructor(
    private readonly oauth: OAuthService,
    private readonly appState: AppStateService,
  ) {}

  /**
   * Called by the Python bridge as an **import method**.
   *
   * Pyodide passes Python keyword arguments as a trailing Map/object,
   * so we accept either:
   *   - `callLLM("prompt")` → args = ["prompt"]
   *   - `callLLM("prompt", system="...", temperature=0.0)` → args = ["prompt", Map{system, temperature}]
   *   - `callLLM("prompt", "system", "model", 1024, 0.5)` → positional (enoch compat)
   *
   * @returns  `{ content, model, usage: { inputTokens, outputTokens }, stopReason }`
   */
  async callLLM(args: any[]): Promise<Record<string, unknown>> {
    const prompt = args[0];
    let system: any;
    let model: any;
    let maxTokens: any;
    let temperature: any;

    const second = args[1];
    if (second instanceof Map) {
      // Pyodide kwargs: Map { "system" => "...", "temperature" => 0.0 }
      system = second.get('system');
      model = second.get('model');
      maxTokens = second.get('max_tokens') ?? second.get('maxTokens');
      temperature = second.get('temperature');
    } else if (second !== undefined && typeof second === 'object' && second !== null && !Array.isArray(second)) {
      // Plain object kwargs (e.g. from PythonHelper.pythonToJs conversion)
      system = second.system;
      model = second.model;
      maxTokens = second.max_tokens ?? second.maxTokens;
      temperature = second.temperature;
    } else {
      // Positional args (enoch compat)
      system = second;
      model = args[2];
      maxTokens = args[3];
      temperature = args[4];
    }

    const projectId = this.appState.projectId();
    if (!projectId) {
      throw new Error('EHQ.callLLM: no active project');
    }

    const body: Record<string, unknown> = {
      messages: [{ role: 'user', content: prompt }],
      stream: false,
    };

    if (system) body['system'] = system;
    if (model) body['model'] = model;
    if (maxTokens) body['maxTokens'] = maxTokens;
    if (temperature !== undefined && temperature !== null) body['temperature'] = temperature;

    const token = this.oauth.getAccessToken();
    const response = await fetch(
      `${API_BASE}/project/${projectId}/llm/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`LLM call failed: ${response.status} ${text}`);
    }

    return await response.json();
  }
}
